import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";
import {
  signInWithPopup,
  onAuthStateChanged,
  signOut,
} from "firebase/auth";
import { auth, googleProvider } from "../../firebase";
import Tree from "./Tree/Tree";
import HistoryDrawer from "./HistoryDrawer";

export const ALLOWED_EMAILS = [
  "wong.antonio@hotmail.com",
  "wong.antonio21@gmail.com",
  "lwongconsulting@gmail.com",
  "wong.rogerio@gmail.com",
];

const TARGET_SITE_UID = "bhzfBvdgxZYrvKvDaXz5iaQzIjn1";

function AdminPage() {
  const [currentUser, setCurrentUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [authError, setAuthError] = useState("");
  const [showSiteHistory, setShowSiteHistory] = useState(false);

  // Preview & View mode state
  const [previewUrl, setPreviewUrl] = useState("/projects");
  const [viewMode, setViewMode] = useState("split"); // "split", "tree", "preview"
  const [refreshKey, setRefreshKey] = useState(0);

  const iframeRef = useRef(null);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, (user) => {
      setCurrentUser(user);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    const handleMsg = (e) => {
      if (e.source === window) return;
      if (e.data?.type === "PREVIEW_URL_CHANGED" && e.data.url) {
        if (e.data.url !== "/admin" && !e.data.url.startsWith("/admin")) {
          setPreviewUrl(e.data.url);
        }
      }
    };
    window.addEventListener("message", handleMsg);
    return () => window.removeEventListener("message", handleMsg);
  }, []);

  const handleSignIn = async () => {
    setAuthError("");
    try {
      const result = await signInWithPopup(auth, googleProvider);
      const email = result.user?.email?.toLowerCase();
      if (!ALLOWED_EMAILS.includes(email)) {
        await signOut(auth);
        setAuthError(`Access denied: "${result.user?.email}" is not an authorized administrator.`);
      }
    } catch (err) {
      console.error("Sign in error:", err);
      setAuthError(err.message || "Failed to sign in with Google.");
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
    } catch (err) {
      console.error("Sign out error:", err);
    }
  };

  const handlePreviewNavigate = (url) => {
    if (!url || url === "/admin" || url.startsWith("/admin")) return;
    setPreviewUrl(url);
    if (viewMode === "tree") {
      setViewMode("split");
    }
    const msg = { type: "NAVIGATE_PREVIEW", url };
    try {
      const bc = new BroadcastChannel("lwong_live_preview");
      bc.postMessage(msg);
      bc.close();
    } catch (e) {}
    if (iframeRef.current?.contentWindow) {
      try {
        iframeRef.current.contentWindow.postMessage(msg, "*");
      } catch (e) {}
    }
  };

  const isAuthorized =
    currentUser &&
    currentUser.email &&
    ALLOWED_EMAILS.includes(currentUser.email.toLowerCase());

  if (loading) {
    return (
      <AuthScreen>
        <AuthCard>
          <LoadingSpinner />
          <p style={{ marginTop: "1rem", color: "#aaa" }}>Verifying authorization...</p>
        </AuthCard>
      </AuthScreen>
    );
  }

  if (!currentUser) {
    return (
      <AuthScreen>
        <AuthCard>
          <AuthTitle>L Wong Consulting</AuthTitle>
          <AuthSubtitle>Admin Content Management</AuthSubtitle>
          {authError && <ErrorMessage>{authError}</ErrorMessage>}
          <SignInButton onClick={handleSignIn}>
            <svg width="20" height="20" viewBox="0 0 24 24" style={{ marginRight: "10px" }}>
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.06H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.94l2.85-2.22.81-.63z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.06l3.66 2.84c.87-2.6 3.3-4.52 6.16-4.52z"
              />
            </svg>
            Sign in with Google
          </SignInButton>
          <BackLink to="/">← Return to Website</BackLink>
        </AuthCard>
      </AuthScreen>
    );
  }

  if (!isAuthorized) {
    return (
      <AuthScreen>
        <AuthCard>
          <AuthTitle style={{ color: "#ff4d4f" }}>Access Denied</AuthTitle>
          <ErrorMessage>
            "{currentUser.email}" is not authorized to access this administration panel.
          </ErrorMessage>
          <SignInButton onClick={handleSignOut} style={{ background: "#333", marginTop: "1rem" }}>
            Sign Out
          </SignInButton>
          <BackLink to="/" style={{ marginTop: "1rem" }}>← Return to Website</BackLink>
        </AuthCard>
      </AuthScreen>
    );
  }

  return (
    <AdminContainer>
      <TopBar>
        <BrandSection>
          <BrandTitle>L Wong Consulting</BrandTitle>
          <Badge>Admin CMS</Badge>
        </BrandSection>

        <ViewModeToggle>
          <ModeButton
            active={viewMode === "split"}
            onClick={() => setViewMode("split")}
            title="Split screen: Tree on left, Preview on right"
          >
            ◫ Split View
          </ModeButton>
          <ModeButton
            active={viewMode === "tree"}
            onClick={() => setViewMode("tree")}
            title="Tree full screen"
          >
            ◧ Tree Only
          </ModeButton>
          <ModeButton
            active={viewMode === "preview"}
            onClick={() => setViewMode("preview")}
            title="Preview full screen"
          >
            ◨ Preview Only
          </ModeButton>
        </ViewModeToggle>

        <ControlsSection>
          <HistoryNavButton onClick={() => setShowSiteHistory(true)} title="View site change history and rollbacks">
            <span>🕒</span>
            <span>Site History</span>
          </HistoryNavButton>
          <NavButton as={Link} to="/" target="_blank" rel="noopener noreferrer">
            Open Site ↗
          </NavButton>
          <SignOutButton onClick={handleSignOut}>
            Sign Out
          </SignOutButton>
        </ControlsSection>
      </TopBar>

      <MainWorkspace>
        {viewMode !== "preview" && (
          <CanvasArea viewMode={viewMode}>
            <TreeContainer>
              <Tree uid={TARGET_SITE_UID} onPreview={handlePreviewNavigate} />
            </TreeContainer>
          </CanvasArea>
        )}

        {viewMode !== "tree" && (
          <PreviewArea viewMode={viewMode}>
            <PreviewHeader>
              <PreviewStatus>
                <LiveDot />
                <PreviewTitle>Live Site Preview</PreviewTitle>
              </PreviewStatus>

              <PreviewNavTabs>
                <TabButton
                  active={previewUrl.startsWith("/projects")}
                  onClick={() => handlePreviewNavigate("/projects")}
                >
                  Projects
                </TabButton>
                <TabButton
                  active={previewUrl === "/"}
                  onClick={() => handlePreviewNavigate("/")}
                >
                  Home
                </TabButton>
                <TabButton
                  active={previewUrl === "/about"}
                  onClick={() => handlePreviewNavigate("/about")}
                >
                  About
                </TabButton>
                <TabButton
                  active={previewUrl === "/Careers"}
                  onClick={() => handlePreviewNavigate("/Careers")}
                >
                  Careers
                </TabButton>
                <TabButton
                  active={previewUrl === "/Contact"}
                  onClick={() => handlePreviewNavigate("/Contact")}
                >
                  Contact
                </TabButton>
              </PreviewNavTabs>

              <PreviewTools>
                <UrlPill title="Current preview path">{previewUrl}</UrlPill>
                <IconBtn
                  onClick={() => setRefreshKey((k) => k + 1)}
                  title="Reload preview"
                >
                  ↻
                </IconBtn>
              </PreviewTools>
            </PreviewHeader>

            <IframeContainer>
              <PreviewIframe
                ref={iframeRef}
                key={`${previewUrl}-${refreshKey}`}
                src={previewUrl}
                title="Live Website Preview"
              />
            </IframeContainer>
          </PreviewArea>
        )}
      </MainWorkspace>

      <HistoryDrawer
        open={showSiteHistory}
        onClose={() => setShowSiteHistory(false)}
      />
    </AdminContainer>
  );
}

export default AdminPage;

const AdminContainer = styled.div`
  display: flex;
  flex-direction: column;
  width: 100vw;
  height: 100vh;
  background-color: #121212;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif;
`;

const TopBar = styled.header`
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 54px;
  padding: 0 18px;
  background-color: #1c1c1e;
  border-bottom: 1px solid #2c2c2e;
  color: white;
  z-index: 1000;
  flex-shrink: 0;
  gap: 12px;
`;

const BrandSection = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  flex-shrink: 0;
`;

const BrandTitle = styled.div`
  font-size: 15px;
  font-weight: 700;
  letter-spacing: 0.4px;
  color: #f5f5f7;
`;

const Badge = styled.span`
  background-color: #2a9d8e;
  color: white;
  font-size: 11px;
  font-weight: 700;
  padding: 2px 8px;
  border-radius: 12px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const ViewModeToggle = styled.div`
  display: flex;
  align-items: center;
  background-color: #121212;
  border: 1px solid #2c2c2e;
  border-radius: 8px;
  padding: 2px;
  gap: 2px;

  @media (max-width: 800px) {
    display: none;
  }
`;

const ModeButton = styled.button`
  background: ${(props) => (props.active ? "#2c2c2e" : "transparent")};
  color: ${(props) => (props.active ? "#ffffff" : "#8e8e93")};
  font-size: 12px;
  font-weight: 600;
  border: none;
  padding: 5px 10px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.15s;

  &:hover {
    color: #ffffff;
  }
`;

const ControlsSection = styled.div`
  display: flex;
  align-items: center;
  gap: 14px;
  flex-shrink: 0;
`;

const UserEmail = styled.span`
  font-size: 13px;
  color: #a1a1a6;
  @media (max-width: 900px) {
    display: none;
  }
`;

const HistoryNavButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: linear-gradient(135deg, rgba(42, 157, 142, 0.25), rgba(42, 157, 142, 0.1));
  border: 1px solid rgba(42, 157, 142, 0.4);
  color: #2a9d8e;
  font-size: 13px;
  font-weight: 600;
  padding: 6px 14px;
  border-radius: 6px;
  cursor: pointer;
  transition: all 0.2s ease;
  user-select: none;

  &:hover {
    background: linear-gradient(135deg, rgba(42, 157, 142, 0.35), rgba(42, 157, 142, 0.18));
    border-color: #2a9d8e;
    color: #48cae4;
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(42, 157, 142, 0.2);
  }

  &:active {
    transform: translateY(0);
  }
`;

const NavButton = styled.a`
  display: inline-flex;
  align-items: center;
  font-size: 13px;
  font-weight: 600;
  color: #e5e5ea;
  text-decoration: none;
  padding: 5px 12px;
  border-radius: 6px;
  background-color: #2c2c2e;
  transition: 0.2s;
  &:hover {
    background-color: #3a3a3c;
    color: white;
  }
`;

const SignOutButton = styled.button`
  font-size: 13px;
  font-weight: 600;
  color: #ff6b6b;
  background-color: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.2);
  padding: 5px 12px;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    background-color: rgba(255, 107, 107, 0.2);
    border-color: rgba(255, 107, 107, 0.4);
  }
`;

const MainWorkspace = styled.div`
  display: flex;
  flex: 1;
  width: 100vw;
  height: calc(100vh - 54px);
  overflow: hidden;
`;

const CanvasArea = styled.main`
  flex: ${(props) => (props.viewMode === "split" ? "1" : "none")};
  width: ${(props) => (props.viewMode === "tree" ? "100%" : "auto")};
  height: 100%;
  overflow: auto;
  padding: 30px 20px;
  background-color: #0a0a0a;
  box-sizing: border-box;
  border-right: ${(props) =>
    props.viewMode === "split" ? "2px solid #232326" : "none"};
`;

const TreeContainer = styled.div`
  display: inline-block;
  width: max-content;
  padding-bottom: 120px;
`;

const PreviewArea = styled.section`
  flex: ${(props) => (props.viewMode === "split" ? "1" : "none")};
  width: ${(props) => (props.viewMode === "preview" ? "100%" : "auto")};
  height: 100%;
  display: flex;
  flex-direction: column;
  background-color: #1c1c1e;
  overflow: hidden;
`;

const PreviewHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 44px;
  padding: 0 14px;
  background-color: #141416;
  border-bottom: 1px solid #28282b;
  gap: 12px;
  flex-shrink: 0;
`;

const PreviewStatus = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LiveDot = styled.div`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: #34c759;
  box-shadow: 0 0 8px #34c759;
`;

const PreviewTitle = styled.span`
  font-size: 13px;
  font-weight: 600;
  color: #f5f5f7;
  white-space: nowrap;
`;

const PreviewNavTabs = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  overflow-x: auto;
  ::-webkit-scrollbar {
    display: none;
  }
`;

const TabButton = styled.button`
  background-color: ${(props) =>
    props.active ? "#2a9d8e" : "rgba(255, 255, 255, 0.08)"};
  color: ${(props) => (props.active ? "#ffffff" : "#c7c7cc")};
  border: none;
  font-size: 12px;
  font-weight: 600;
  padding: 4px 10px;
  border-radius: 4px;
  cursor: pointer;
  white-space: nowrap;
  transition: 0.15s;

  &:hover {
    background-color: ${(props) =>
      props.active ? "#238276" : "rgba(255, 255, 255, 0.15)"};
    color: #ffffff;
  }
`;

const PreviewTools = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const UrlPill = styled.div`
  font-size: 11px;
  font-family: monospace;
  color: #8e8e93;
  background-color: rgba(255, 255, 255, 0.05);
  padding: 3px 8px;
  border-radius: 4px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  max-width: 140px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @media (max-width: 1100px) {
    display: none;
  }
`;

const IconBtn = styled.button`
  background: none;
  border: 1px solid rgba(255, 255, 255, 0.15);
  color: #e5e5ea;
  width: 26px;
  height: 26px;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 14px;
  transition: 0.15s;

  &:hover {
    background-color: rgba(255, 255, 255, 0.1);
    color: white;
  }
`;

const IframeContainer = styled.div`
  flex: 1;
  width: 100%;
  height: 100%;
  background-color: white;
  position: relative;
`;

const PreviewIframe = styled.iframe`
  width: 100%;
  height: 100%;
  border: none;
  display: block;
`;

const AuthScreen = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100vw;
  height: 100vh;
  background-color: #0d0d0d;
`;

const AuthCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 40px;
  background-color: #1a1a1a;
  border-radius: 12px;
  border: 1px solid #2d2d2d;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.5);
  max-width: 400px;
  width: 90%;
  text-align: center;
`;

const AuthTitle = styled.h1`
  font-size: 22px;
  font-weight: 600;
  color: #fff;
  margin: 0 0 8px 0;
`;

const AuthSubtitle = styled.p`
  font-size: 14px;
  color: #888;
  margin: 0 0 24px 0;
`;

const SignInButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  padding: 12px;
  font-size: 14px;
  font-weight: 500;
  color: #fff;
  background-color: #24292e;
  border: 1px solid #444;
  border-radius: 6px;
  cursor: pointer;
  transition: 0.2s;
  &:hover {
    background-color: #2f363d;
    border-color: #666;
  }
`;

const BackLink = styled(Link)`
  margin-top: 20px;
  font-size: 13px;
  color: #2a9d8e;
  text-decoration: none;
  &:hover {
    text-decoration: underline;
  }
`;

const ErrorMessage = styled.div`
  color: #ff6b6b;
  font-size: 13px;
  background: rgba(255, 107, 107, 0.1);
  border: 1px solid rgba(255, 107, 107, 0.2);
  padding: 10px 14px;
  border-radius: 6px;
  margin-bottom: 16px;
  line-height: 1.4;
`;

const LoadingSpinner = styled.div`
  width: 36px;
  height: 36px;
  border: 3px solid rgba(255, 255, 255, 0.1);
  border-top-color: #2a9d8e;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;

  @keyframes spin {
    to {
      transform: rotate(360deg);
    }
  }
`;
